import BlockCodeRuntime from '@/components/code/blockCode/BlockCodeRuntime';
import ErrorComponent from '@/components/ui/custom/ErrorComponent';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';
import useActivityView from '@/data/hook/useActivityView';
import useAuth from '@/data/hook/useAuth';
import { updateBlockActivityProgress } from '@/data/services/activityElement/blockActivityProgressServices';
import {
  blockActivitiesQueryKeys,
  blockProgressQueryKeys,
} from '@/data/services/querykeys';
import { isStudent } from '@/functions/auth';
import { ActivityTypeEnum } from '@/models/_deprecated/ActivityElement';
import { BlockCodeActivityElement as BlockCodeActivity } from '@/models/_deprecated/ActivityElement';
import { BlockCodeActivityProgress } from '@/models/ActivityProgress';
import LoadingView from '@/pages/courses/LoadingView';
import alert from '@/utils/shared/alert';
import { getWorkspace, jsFixGenerator } from '@/utils/blockQuestion';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { Workspace, blockCodeEditorSerializer } from '@cna-br/astro-code';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { PropsWithChildren, useEffect, useRef } from 'react';
import {
  useChangeWorkspace,
  useWorkspace,
} from '../../edit/BlockCodeElementEdit/WorkspaceForm.hooks';
import { useAnswerDebounce } from '../ActivitiesPreview.hooks';
import ActivityHeading from '../ActivityHeading';
import { ElementPreviewFactoryProps } from '../ElementPreviewFactory';

function BlockCodeActivityElement({
  progressId,
  questionId,
  activityMode,
  attemptElement,
}: ElementPreviewFactoryProps) {
  const { user } = useAuth();

  const studentView = isStudent(user?.userType);

  if (studentView) {
    return (
      <StudentView
        key={progressId}
        progressId={progressId}
        questionId={questionId}
        activityMode={activityMode}
        attemptElement={attemptElement}
      />
    );
  }

  return <StaffView key={questionId} questionId={questionId} />;
}

function StudentView({
  progressId,
  questionId: activityId,
  attemptElement,
}: Pick<
  ElementPreviewFactoryProps,
  'questionId' | 'progressId' | 'activityMode' | 'attemptElement' | 'answer'
>) {
  const { debounce: debounceAnswer } = useAnswerDebounce(500);

  const {
    invalidate,
    blockStepNavigate,
    mode,
    finishObserver: resetObserver,
  } = useActivityView();
  const { queryFn, queryKey } = blockProgressQueryKeys.get({
    progressId,
    activityId,
  });

  const {
    data: blockProgress,
    isInitialLoading: isLoading,
    error,
  } = useQuery({
    queryFn,
    queryKey,
    enabled: mode === 'activity' || mode === 'answerkey',
  });

  const cacheManager = useSingleCache<BlockCodeActivityProgress>(queryKey);

  const subscribeIdRef = useRef<string>();
  const activity = blockProgress?.blockActivity;

  const workspaceJson = getWorkspace({
    activity,
    blockProgress,
    attemptElement,
    mode,
  });

  const saveAnswer = async (workspace: Workspace) => {
    blockStepNavigate(true);
    if (!workspace) return;

    const workspaceJson = blockCodeEditorSerializer.save(workspace);
    const answer = jsFixGenerator(workspace);
    const newProgress = {
      answerWorkspace: workspaceJson,
      answer,
      isDone: true,
    };

    cacheManager.merge(newProgress);
    return await updateBlockActivityProgress(
      { activityId, progressId },
      newProgress,
    );
  };

  const { mutate: updateAnswer } = useMutation(saveAnswer, {
    async onSuccess(data) {
      await invalidate(ActivityTypeEnum.BLOCK, data);
    },

    onError(error) {
      cacheManager.undo();
      alert.error(getErrorMessage(error));
    },
    onSettled() {
      blockStepNavigate(false);
    },
  });

  const onChange = (newWorkspace: Workspace) => {
    if (mode !== 'activity') return;
    debounceAnswer(() => updateAnswer(newWorkspace));
  };

  const { workspace, onWorkspaceChange } = useChangeWorkspace(
    workspaceJson,
    onChange,
  );

  useEffect(() => {
    if (subscribeIdRef.current || !blockProgress) return;

    subscribeIdRef.current = resetObserver.subscribe(() => {
      cacheManager.set({
        ...blockProgress,
        answer: null,
        answerWorkspace: null,
      });
    });
  }, [resetObserver, cacheManager, blockProgress]);
  return (
    <ActivityDisplay isLoading={isLoading} error={error} activity={activity}>
      <BlockCodeRuntime
        workspace={workspace}
        onWorkspaceChange={onWorkspaceChange}
        maxBlocks={activity?.maxBlocks}
        readOnly={mode !== 'activity'}
        className="h-[31.25rem]"
      />
    </ActivityDisplay>
  );
}

type StaffViewProps = {
  questionId: number;
};

function StaffView({ questionId }: StaffViewProps) {
  const {
    data: blockActivity,
    isLoading,
    error,
  } = useQuery(blockActivitiesQueryKeys.get(questionId));

  const { workspace, setWorkspace } = useWorkspace(
    blockActivity?.initialWorkspace,
  );

  return (
    <ActivityDisplay
      activity={blockActivity}
      isLoading={isLoading}
      error={error}
    >
      <BlockCodeRuntime
        workspace={workspace}
        onWorkspaceChange={setWorkspace}
        maxBlocks={blockActivity?.maxBlocks}
        readOnly={true}
        className="h-[500px]"
      />
    </ActivityDisplay>
  );
}

type ActivityDisplayProps = {
  activity?: BlockCodeActivity;
  isLoading?: boolean;
  error?: unknown;
};

const ActivityDisplay: React.FC<PropsWithChildren<ActivityDisplayProps>> = ({
  activity,
  error,
  isLoading,
  children,
}) => {
  const errorDetail = getErrorMessage(error);

  if (isLoading && !activity) {
    return <LoadingView />;
  }
  if (error || !activity) {
    return <ErrorComponent errorTextTitle={errorDetail} />;
  }

  return (
    <div className="flex flex-col gap-1 w-11/12">
      <ActivityHeading
        heading={activity.title}
        content={activity.instructions}
      />
      {children}
    </div>
  );
};

export default BlockCodeActivityElement;
